'use client'

import { motion } from 'framer-motion'
import { ArrowRight } from 'lucide-react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'

import { getSessionBySlug } from '@shared/gallery-sessions'

import { BorderBeam } from '@client/components/ui/border-beam'
import { Button } from '@client/components/ui/button'

const containerClasses = 'container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'

export default function EvidenceLayout({ children }: { children: React.ReactNode }): React.ReactElement {
  // Newsroom posts render under /evidence/<slug> and so inherit this layout,
  // but they are news rather than evidence: "See the Evidence in Your Own
  // Work" is the wrong ask at the end of an announcement. Blog posts get the
  // homepage's framing instead. Everything else under /evidence (benchmarks,
  // case studies, arena) keeps the evidence CTA.
  const slug = usePathname()?.split('/')[2]
  const isNewsroomPost = !!slug && getSessionBySlug(slug)?.isBlogPost === true
  const heading = isNewsroomPost
    ? 'Catch critical issues in research before publication'
    : 'See the Evidence in Your Own Work'

  return (
    <div className="flex-1 flex flex-col">
      <section className={`${containerClasses} pt-8 pb-6`}>{children}</section>

      {/* Free Review CTA */}
      <section className="py-16 sm:py-20">
        <div className="container max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="relative overflow-hidden text-center bg-gradient-to-br from-primary/5 to-primary/10 rounded-2xl p-12 border"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{heading}</h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Upload a paper or grant and get a full review in minutes.
            </p>

            <Button size="lg" className="text-lg px-8" asChild>
              <Link href="/upload">
                Get a Review
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <BorderBeam duration={8} size={100} />
          </motion.div>
        </div>
      </section>
    </div>
  )
}
